import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useFacility } from '../../context/FacilityContext';
import {
  listCorporateMenus,
  getCorporateMenuVersion,
  createOrUpdateCorporateMenu,
} from '../../firebase/functions';
import { getGlobalStartDate, formatSeason } from '../../utils/corporateMenu';
import './CorporateMenu.css';

function CorporateMenu() {
  const { claims, currentUser, userLoading } = useAuth();
  const { facilityId } = useFacility();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  const [region, setRegion] = useState('speelman');
  const [season, setSeason] = useState('spring-summer');
  const [year, setYear] = useState(new Date().getFullYear());
  const [view, setView] = useState('total');
  const [selectedWeek, setSelectedWeek] = useState(1);

  const [availableMenus, setAvailableMenus] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [menuData, setMenuData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  const isSuperAdmin = claims?.superadmin === true;

  useEffect(() => {
    const fetchMenus = async () => {
      setIsLoading(true);
      setError('');
      try {
        const result = await listCorporateMenus({ region });
        setAvailableMenus(result.data.menus);
      } catch (err) {
        setError(err?.message || 'Failed to load corporate menus');
      } finally {
        setIsLoading(false);
      }
    };

    // Wait for auth to be ready to ensure callable includes auth token
    if (!userLoading && currentUser) {
      fetchMenus();
    }
  }, [region, userLoading, currentUser]);

  // Keep selectedVersion in sync when season/year change
  useEffect(() => {
    const currentMenu = availableMenus.find(
      (m) => m.season === season && m.year === year,
    );
    if (currentMenu && currentMenu.versions?.length > 0) {
      setSelectedVersion(currentMenu.versions[0].versionId);
      setError('');
      setHasAttemptedLoad(true);
    } else {
      setSelectedVersion('');
      setMenuData(null);
      setIsEditing(true);
      setError('');
      setHasAttemptedLoad(true);
    }
  }, [season, year, availableMenus]);

  useEffect(() => {
    if (!selectedVersion) {
      setMenuData(null);
      return;
    }

    const fetchMenuData = async () => {
      setIsLoading(true);
      setError('');
      try {
        const result = await getCorporateMenuVersion({
          region,
          season,
          year,
          versionId: selectedVersion,
        });
        setMenuData(result.data.menu);
      } catch (err) {
        setError(err?.message || 'Failed to load menu data');
      } finally {
        setIsLoading(false);
      }
    };

    if (!userLoading && currentUser) {
      fetchMenuData();
    }
  }, [selectedVersion, userLoading, currentUser]);

  const currentVersions =
    availableMenus.find((m) => m.season === season && m.year === year)
      ?.versions || [];

  const globalStartDate = getGlobalStartDate(season, year);

  const yearsAvailable = Array.from(
    new Set(availableMenus.map((m) => m.year)),
  ).sort((a, b) => a - b);

  const handleCellChange = (dayIndex, mealType, value) => {
    if (!menuData || !isEditing) return;
    const updated = { ...menuData };
    // Store raw text, don't process it yet
    updated.days[dayIndex][mealType].rawText = value;
    setMenuData(updated);
  };

  const initializeEmptyMenu = () => {
    const emptyDays = Array.from({ length: 28 }, (_, idx) => ({
      dayIndex: idx,
      breakfast: { items: [] },
      lunch: { items: [] },
      dinner: { items: [] },
    }));
    setMenuData({
      region,
      season,
      year,
      versionId: '',
      status: 'draft',
      days: emptyDays,
      alternates: { items: [] },
    });
    setIsEditing(true);
    setSelectedVersion('');
  };

  const handleSave = async () => {
    if (!isSuperAdmin || !menuData) return;
    setIsLoading(true);
    setSaveStatus('');
    try {
      // Process rawText into items before saving
      const processedDays = menuData.days.map((day) => ({
        ...day,
        breakfast: {
          items: (day.breakfast.rawText || day.breakfast.items.join('\n'))
            .split('\n')
            .map((x) => x.trim())
            .filter(Boolean),
        },
        lunch: {
          items: (day.lunch.rawText || day.lunch.items.join('\n'))
            .split('\n')
            .map((x) => x.trim())
            .filter(Boolean),
        },
        dinner: {
          items: (day.dinner.rawText || day.dinner.items.join('\n'))
            .split('\n')
            .map((x) => x.trim())
            .filter(Boolean),
        },
      }));

      const processedAlternates = {
        items: (
          menuData.alternates?.rawText ||
          menuData.alternates?.items?.join('\n') ||
          ''
        )
          .split('\n')
          .map((x) => x.trim())
          .filter(Boolean),
      };

      const result = await createOrUpdateCorporateMenu({
        region,
        season,
        year,
        days: processedDays,
        alternates: processedAlternates,
        status: menuData.status || 'draft',
      });
      if (result.data.success) {
        setSaveStatus(result.data.message);
        setSelectedVersion(result.data.versionId);
        setIsEditing(false);
        setTimeout(() => setSaveStatus(''), 3000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to save corporate menu');
    } finally {
      setIsLoading(false);
    }
  };

  const renderDayCell = (dayIndex) => {
    if (!menuData || !menuData.days || !menuData.days[dayIndex]) {
      return (
        <Box className="day-cell empty">
          <Typography variant="caption">No data</Typography>
        </Box>
      );
    }

    const day = menuData.days[dayIndex];

    const renderEditable = (mealType, label) => {
      // Use rawText if available (during editing), otherwise join items
      const value =
        day[mealType].rawText !== undefined
          ? day[mealType].rawText
          : day[mealType].items.join('\n');
      return isEditing ? (
        <textarea
          className="meal-textarea"
          value={value}
          placeholder={`${label} items (one per line)`}
          onChange={(e) => handleCellChange(dayIndex, mealType, e.target.value)}
        />
      ) : (
        day[mealType].items.map((item, idx) => (
          <Typography key={idx} variant="body2" className="meal-item">
            {item}
          </Typography>
        ))
      );
    };

    return (
      <Box className="day-cell">
        <Box className="day-number">{dayIndex + 1}</Box>
        <Box className="meal-section">
          <Typography variant="caption" className="meal-label">
            BREAKFAST
          </Typography>
          {renderEditable('breakfast', 'Breakfast')}
        </Box>
        <Box className="meal-section">
          <Typography variant="caption" className="meal-label">
            LUNCH
          </Typography>
          {renderEditable('lunch', 'Lunch')}
        </Box>
        <Box className="meal-section">
          <Typography variant="caption" className="meal-label">
            DINNER
          </Typography>
          {renderEditable('dinner', 'Dinner')}
        </Box>
      </Box>
    );
  };

  const render28DayGrid = () => {
    const weeks = [];
    for (let week = 0; week < 4; week++) {
      const days = [];
      for (let day = 0; day < 7; day++) {
        const dayIndex = week * 7 + day;
        days.push(
          <Box key={dayIndex} className="grid-day">
            {renderDayCell(dayIndex)}
          </Box>,
        );
      }
      weeks.push(
        <Box key={week} className="grid-week">
          {days}
        </Box>,
      );
    }
    return <Box className="calendar-grid-28">{weeks}</Box>;
  };

  const renderWeeklyView = () => {
    const startIdx = (selectedWeek - 1) * 7;
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(
        <Box key={i} className="weekly-day">
          <Typography variant="h6" className="day-header">
            Day {startIdx + i + 1}
          </Typography>
          {renderDayCell(startIdx + i)}
        </Box>,
      );
    }
    return <Box className="weekly-grid">{days}</Box>;
  };

  return (
    <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
      <Box className="corporate-menu-container">
        <Box className="corporate-menu-header">
          <Typography variant="h4" className="page-title">
            Overview - Corporate Menu
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper className="controls-paper" elevation={0}>
          <Box className="controls-section">
            <Typography variant="subtitle2" className="control-label">
              Menu:
            </Typography>
            <ToggleButtonGroup
              value={season}
              exclusive
              onChange={(e, val) => val && setSeason(val)}
              className="season-toggle"
            >
              <ToggleButton value="spring-summer">Spring & Summer</ToggleButton>
              <ToggleButton value="fall-winter">Fall & Winter</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box className="controls-section">
            <Typography variant="subtitle2" className="control-label">
              View:
            </Typography>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(e, val) => val && setView(val)}
              className="view-toggle"
            >
              <ToggleButton value="total">Total</ToggleButton>
              <ToggleButton value="weekly">Weekly</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box className="controls-section">
            <Typography variant="subtitle2" className="control-label">
              Region:
            </Typography>
            <FormControl size="small" className="region-select">
              <Select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                displayEmpty
              >
                <MenuItem value="speelman">Speelman</MenuItem>
                <MenuItem value="homa">HOMA</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box className="controls-section">
            <Typography variant="subtitle2" className="control-label">
              Year:
            </Typography>
            <FormControl size="small" className="year-select">
              <Select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                displayEmpty
              >
                {yearsAvailable.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
                {[...Array(10)].map((_, idx) => {
                  const futureYear = new Date().getFullYear() + idx;
                  if (!yearsAvailable.includes(futureYear)) {
                    return (
                      <MenuItem key={futureYear} value={futureYear}>
                        {futureYear}
                      </MenuItem>
                    );
                  }
                  return null;
                })}
              </Select>
            </FormControl>
          </Box>

          {view === 'weekly' && (
            <Box className="controls-section">
              <Typography variant="subtitle2" className="control-label">
                Menu:
              </Typography>
              <ToggleButtonGroup
                value={selectedWeek}
                exclusive
                onChange={(e, val) => val && setSelectedWeek(val)}
                className="week-toggle"
              >
                <ToggleButton value={1}>Week 1</ToggleButton>
                <ToggleButton value={2}>Week 2</ToggleButton>
                <ToggleButton value={3}>Week 3</ToggleButton>
                <ToggleButton value={4}>Week 4</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Paper>

        {saveStatus && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {saveStatus}
          </Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : menuData ? (
          <Box className="menu-content">
            {view === 'total' ? render28DayGrid() : renderWeeklyView()}

            {isEditing && menuData && (
              <Paper className="alternates-section" elevation={0}>
                <Typography variant="h6" className="alternates-title">
                  Alternates
                </Typography>
                <textarea
                  className="meal-textarea alternates-textarea"
                  value={
                    menuData.alternates?.rawText !== undefined
                      ? menuData.alternates.rawText
                      : menuData.alternates?.items?.join('\n') || ''
                  }
                  placeholder="Alternate items (one per line)"
                  onChange={(e) =>
                    setMenuData({
                      ...menuData,
                      alternates: {
                        ...menuData.alternates,
                        rawText: e.target.value,
                      },
                    })
                  }
                />
              </Paper>
            )}
            {!isEditing &&
              menuData.alternates &&
              menuData.alternates.items.length > 0 && (
                <Paper className="alternates-section" elevation={0}>
                  <Typography variant="h6" className="alternates-title">
                    Alternates
                  </Typography>
                  <Box className="alternates-list">
                    {menuData.alternates.items.map((item, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        className="alternate-item"
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              )}
          </Box>
        ) : isSuperAdmin && !isLoading && hasAttemptedLoad ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              No corporate menu found for {formatSeason(season)} {year}. Create
              one below.
            </Alert>
            <Button variant="contained" onClick={initializeEmptyMenu}>
              Start New Corporate Menu
            </Button>
          </Box>
        ) : !isLoading && hasAttemptedLoad ? (
          <Alert severity="warning">
            No corporate menu found for {formatSeason(season)} {year}
          </Alert>
        ) : null}
        {isSuperAdmin && menuData && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              disabled={isLoading || !isEditing}
              onClick={handleSave}
            >
              {selectedVersion ? 'Save Changes' : 'Create Corporate Menu'}
            </Button>
            <Button
              variant="outlined"
              disabled={isLoading || !isEditing}
              onClick={() => setIsEditing(false)}
            >
              Cancel Edit
            </Button>
            {!isEditing && (
              <Button variant="text" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
}

export default CorporateMenu;
